/**
 * ComboPreview Component
 * Renders a dual-axis combo chart using visx — bars on left axis, line on right axis
 */

import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { GridRows } from '@visx/grid';
import { AxisLeft, AxisRight } from '@visx/axis';
import { useTheme } from '../../contexts/ThemeContext';
import { useData } from '../../contexts/DataContext';
import transformData from '../../utils/transformData';
import { useChartTooltip } from './useChartTooltip';
import AxisLaneDebugOverlay from './AxisLaneDebugOverlay';
import ChartTitle from './ChartTitle';
import {
  resolveXAxisLabels,
  computeChartLayout,
  computeAxisLanes,
  getAxisSpacingSpec,
  isAxisDebugEnabled,
  reportAxisLaneClamp,
  computeTitleFontSize,
  LEGEND_SPACING, legendTopTranslateY, legendTopSpace,
  formatCompactNumber,
  getCompactTickFormatter,
} from '../../utils/chartLayout';
import {
  getSeriesDashSvg,
  getSeriesMarkerSvg,
} from '../../utils/accessibility';
import { getComboColorPair } from '../../charts/shared/palettes';

function renderMarkerShape({ shape, x, y, size, fill, stroke }) {
  const s = size;
  const props = { fill, stroke, strokeWidth: 1 };
  if (shape === 'square') return <rect x={x - s} y={y - s} width={s * 2} height={s * 2} {...props} />;
  if (shape === 'triangle') return <polygon points={`${x},${y - s} ${x - s},${y + s} ${x + s},${y + s}`} {...props} />;
  if (shape === 'diamond') return <polygon points={`${x},${y - s} ${x - s},${y} ${x},${y + s} ${x + s},${y}`} {...props} />;
  if (shape === 'triangleDown') return <polygon points={`${x - s},${y - s} ${x + s},${y - s} ${x},${y + s}`} {...props} />;
  if (shape === 'plus') {
    const t = s * 0.35;
    return <polygon points={`${x - t},${y - s} ${x + t},${y - s} ${x + t},${y - t} ${x + s},${y - t} ${x + s},${y + t} ${x + t},${y + t} ${x + t},${y + s} ${x - t},${y + s} ${x - t},${y + t} ${x - s},${y + t} ${x - s},${y - t} ${x - t},${y - t}`} {...props} />;
  }
  if (shape === 'cross') {
    const a = s * 0.7, t = s * 0.25;
    return <polygon points={`${x},${y - t} ${x + a - t},${y - a} ${x + a},${y - a + t} ${x + t},${y} ${x + a},${y + a - t} ${x + a - t},${y + a} ${x},${y + t} ${x - a + t},${y + a} ${x - a},${y + a - t} ${x - t},${y} ${x - a},${y - a + t} ${x - a + t},${y - a}`} {...props} />;
  }
  if (shape === 'hexagon') {
    const pts = [0, 1, 2, 3, 4, 5].map(i => { const a = Math.PI / 3 * i - Math.PI / 6; return `${x + s * Math.cos(a)},${y + s * Math.sin(a)}`; }).join(' ');
    return <polygon points={pts} {...props} />;
  }
  if (shape === 'pentagon') {
    const pts = [0, 1, 2, 3, 4].map(i => { const a = (2 * Math.PI / 5) * i - Math.PI / 2; return `${x + s * Math.cos(a)},${y + s * Math.sin(a)}`; }).join(' ');
    return <polygon points={pts} {...props} />;
  }
  if (shape === 'star') {
    const pts = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => { const a = (Math.PI / 5) * i - Math.PI / 2; const r = i % 2 === 0 ? s : s * 0.45; return `${x + r * Math.cos(a)},${y + r * Math.sin(a)}`; }).join(' ');
    return <polygon points={pts} {...props} />;
  }
  return <circle cx={x} cy={y} r={s} {...props} />;
}

function PatternDefs({ idPrefix, color }) {
  return (
    <defs>
      <pattern id={`${idPrefix}-0`} patternUnits="userSpaceOnUse" width="8" height="8">
        <rect width="8" height="8" fill={color} />
        <path d="M0,8 L8,0 M-2,2 L2,-2 M6,10 L10,6" stroke="#00000055" strokeWidth="1" />
      </pattern>
    </defs>
  );
}

function ComboPreview({ chartType, config, width, height }) {
  const { resolved: theme } = useTheme();
  const { isLoaded, derivedData: userData, mapping } = useData();

  // Get data — user data or sample data
  const data = useMemo(() => {
    if (isLoaded && userData.length > 0 && Object.keys(mapping).length > 0) {
      return transformData(userData, mapping, chartType.id);
    }
    if (chartType.getSampleData) return chartType.getSampleData();
    return chartType.sampleData;
  }, [isLoaded, userData, mapping, chartType]);

  // Adaptive label strategy
  const categories = data.map(d => d.name);
  const showA11yLegend = !!(config.useSeriesStyles || config.usePatternFills);
  const xAxis = useMemo(() => resolveXAxisLabels({
    labels: categories,
    chartWidth: width,
    chartHeight: height,
    baseFontSize: theme.tickSize ?? 13,
    fontFamily: theme.fontBody,
    rotationOverride: config.labelRotation,
    truncateLabels: !!config.truncateXAxisLabels,
    estimatedMargins: 130,
  }), [categories, width, height, theme.tickSize, theme.fontBody, config.labelRotation, config.truncateXAxisLabels]);

  const leftTicks = useMemo(() => {
    const maxBarVal = Math.max(...data.map(d => d.barValue), 0);
    return scaleLinear({ domain: [0, maxBarVal * 1.15], range: [100, 0], nice: true }).ticks();
  }, [data]);
  const lineVals = useMemo(() => data.map(d => d.lineValue), [data]);
  const minLineVal = useMemo(() => Math.min(...lineVals), [lineVals]);
  const maxLineVal = useMemo(() => Math.max(...lineVals), [lineVals]);
  const rightTicksForLayout = useMemo(
    () => scaleLinear({ domain: [minLineVal * 0.85, maxLineVal * 1.1], range: [100, 0], nice: true }).ticks(5),
    [minLineVal, maxLineVal],
  );
  const spacingSpec = getAxisSpacingSpec(theme);
  const {
    xTickGap,
    yTickGap,
    yTickBaselineNudge,
    yTickLength,
  } = spacingSpec;
  const rightTickDx = yTickGap;
  const rightLanesForLayout = computeAxisLanes({
    axisSide: 'right',
    labels: rightTicksForLayout.map((v) => String(v)),
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    showTicks: true,
    spacingSpec,
    telemetryMeta: {
      chartId: 'combo-chart',
      viewportWidth: width,
      viewportHeight: height,
    },
  });
  const axisDebugEnabled = isAxisDebugEnabled();
  if (axisDebugEnabled) reportAxisLaneClamp(rightLanesForLayout);
  const responsiveRightBase = Math.max(80, rightLanesForLayout.requiredMarginPx);

  const { margin, innerWidth, innerHeight } = useMemo(() => computeChartLayout({
    width,
    height,
    title: config.title,
    titleFontSize: computeTitleFontSize(config.title, width) || undefined,
    xLabels: categories,
    yTickValues: leftTicks,
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    hasXLabel: !!config.xlabel,
    hasYLabel: !!config.ylabel,
    orientation: 'vertical',
    baseRight: responsiveRightBase,
    baseBottom: 70,
    legend: {
      show: showA11yLegend,
      position: 'top',
      topSpace: legendTopSpace(1) + 6,
      rightSpace: 0,
      bottomSpace: 0,
    },
    xAxisResolved: xAxis,
    extra: { left: 0 },
  }), [width, height, config.title, config.xlabel, config.ylabel, categories, leftTicks, responsiveRightBase, theme.tickSize, theme.fontBody, xAxis, showA11yLegend]);

  // X scale (categories, band)
  const xScale = useMemo(() => scaleBand({
    domain: data.map(d => d.name),
    range: [0, innerWidth],
    padding: 0.35,
  }), [data, innerWidth]);

  // Left Y scale (bar values)
  const maxBarVal = useMemo(() => Math.max(...data.map(d => d.barValue), 0), [data]);
  const leftScale = useMemo(() => scaleLinear({
    domain: [0, maxBarVal * 1.15],
    range: [innerHeight, 0],
    nice: true,
  }), [maxBarVal, innerHeight]);

  // Right Y scale (line values)
  const rightScale = useMemo(() => scaleLinear({
    domain: [minLineVal * 0.85, maxLineVal * 1.1],
    range: [innerHeight, 0],
    nice: true,
  }), [minLineVal, maxLineVal, innerHeight]);

  const colorPair = getComboColorPair(config, theme);
  const barColor = colorPair.primary;
  const lineColor = colorPair.secondary;
  const lineWidth = config.lineWidth || 2;
  const lineDash = getSeriesDashSvg(0, config.useSeriesStyles);
  const lineMarkerShape = getSeriesMarkerSvg(0, config.useSeriesStyles);
  const bandwidth = xScale.bandwidth();

  // Build SVG polyline points for the line series
  const linePoints = data.map(d => {
    const cx = (xScale(d.name) || 0) + bandwidth / 2;
    const cy = rightScale(d.lineValue);
    return `${cx},${cy}`;
  }).join(' ');

  // ─── Tooltip ───────────────────────────────────────────────────────────────
  const { containerRef, showTip, hideTooltip, Tip } = useChartTooltip(theme);

  // Axis label styling
  const tickLabelProps = () => ({
    fill: theme.tickColor || theme.foreground || '#000',
    fontSize: theme.tickSize ?? 13,
    fontFamily: theme.fontBody || theme.fontHeading || 'Cohere Text',
  });

  const axisLabelProps = {
    fill: theme.labelColor || theme.foreground || '#000',
    fontSize: theme.labelSize ?? 15,
    fontFamily: theme.fontBody || theme.fontHeading || 'Cohere Text',
  };
  const leftLanes = useMemo(() => computeAxisLanes({
    axisSide: 'left',
    labels: leftTicks.map((v) => String(v)),
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    showTicks: true,
    tickLength: yTickLength,
    availableMargin: margin.left,
    spacingSpec: { ...spacingSpec, gapY: yTickGap },
    telemetryMeta: { chartId: 'combo-chart', viewportWidth: width, viewportHeight: height },
  }), [leftTicks, theme.tickSize, theme.fontBody, theme.labelSize, yTickLength, margin.left, spacingSpec, yTickGap, width, height]);
  if (axisDebugEnabled) reportAxisLaneClamp(leftLanes);
  const xLanes = computeAxisLanes({
    axisSide: 'bottom',
    labels: categories.map((c) => xAxis.tickFormat(c)),
    rotation: xAxis.rotation || 0,
    tickFontSize: xAxis.fontSize,
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    showTicks: false,
    tickLength: 0,
    spacingSpec: { ...spacingSpec, gapX: xTickGap },
    telemetryMeta: { chartId: 'combo-chart', viewportWidth: width, viewportHeight: height },
  });
  if (axisDebugEnabled) reportAxisLaneClamp(xLanes);
  const rightLanes = computeAxisLanes({
    axisSide: 'right',
    labels: rightTicksForLayout.map((v) => String(v)),
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    titleFontSize: theme.labelSize ?? 15,
    showTicks: true,
    availableMargin: margin.right,
    spacingSpec,
    telemetryMeta: {
      chartId: 'combo-chart',
      viewportWidth: width,
      viewportHeight: height,
    },
  });
  if (axisDebugEnabled) reportAxisLaneClamp(rightLanes);
  const rightLabelX = margin.left + innerWidth + rightLanes.titleOffsetPx;
  const rightLabelY = margin.top + innerHeight / 2;

  return (
    <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
      {/* Title */}
      <ChartTitle title={config.title} chartWidth={width} marginLeft={margin.left} marginRight={margin.right} theme={theme} />

      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} overflow="visible" onMouseLeave={hideTooltip}>
        <Group left={margin.left} top={margin.top}>
          {config.usePatternFills && <PatternDefs idPrefix="combo-pattern" color={barColor} />}
          {showA11yLegend && (() => {
            const rs = LEGEND_SPACING.rectSize;
            const ll = LEGEND_SPACING.lineLength;
            return (
              <g transform={`translate(${Math.max(0, innerWidth - 190)}, ${legendTopTranslateY(1, width, height)})`}>
                <g transform="translate(0,0)">
                  <rect x={0} y={-rs + 2} width={rs} height={rs} fill={config.usePatternFills ? 'url(#combo-pattern-0)' : barColor} />
                  <text x={rs + 6} y={0} fill={theme.legendTextColor} fontSize={theme.legendSize ?? 12} fontFamily={theme.fontBody}>
                    {config.leftLabel || 'Bars'}
                  </text>
                </g>
                <g transform="translate(98,0)">
                  <line x1={0} y1={-4} x2={ll} y2={-4} stroke={lineColor} strokeWidth={lineWidth} strokeDasharray={lineDash} />
                  {renderMarkerShape({
                    shape: lineMarkerShape,
                    x: Math.round(ll / 2),
                    y: -4,
                    size: LEGEND_SPACING.lineMarkerSize,
                    fill: lineColor,
                    stroke: theme.axisColor || '#000',
                  })}
                  <text x={ll + 6} y={0} fill={theme.legendTextColor} fontSize={theme.legendSize ?? 12} fontFamily={theme.fontBody}>
                    {config.rightLabel || 'Line'}
                  </text>
                </g>
              </g>
            );
          })()}
          {/* Grid (based on left / bar scale) */}
          {config.showGrid && (
            <GridRows
              scale={leftScale}
              width={innerWidth}
              stroke={theme.gridColor || '#CCCCCC'}
              strokeOpacity={theme.gridAlpha ?? 0.3}
              strokeDasharray="3 3"
            />
          )}

          {/* Bars */}
          {data.map((d, i) => {
            const x = xScale(d.name) || 0;
            const barHeight = innerHeight - leftScale(d.barValue);
            const y = innerHeight - barHeight;
            return (
              <rect
                key={`bar-${i}`}
                x={x}
                y={y}
                width={bandwidth}
                height={Math.max(0, barHeight)}
                fill={config.usePatternFills ? 'url(#combo-pattern-0)' : barColor}
                style={{ cursor: 'pointer' }}
                onMouseMove={(e) => showTip(e, `${formatCompactNumber(d.barValue, 1)} · ${formatCompactNumber(d.lineValue, 1)}`)}
              />
            );
          })}

          {/* Line */}
          <polyline
            points={linePoints}
            fill="none"
            stroke={lineColor}
            strokeWidth={lineWidth}
            strokeDasharray={lineDash}
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* Markers */}
          {config.showMarkers && data.map((d, i) => (
            <g
              key={`marker-${i}`}
              style={{ cursor: 'pointer' }}
              onMouseMove={(e) => showTip(e, `${formatCompactNumber(d.barValue, 1)} · ${formatCompactNumber(d.lineValue, 1)}`)}
            >
              {renderMarkerShape({
                shape: lineMarkerShape,
                x: (xScale(d.name) || 0) + bandwidth / 2,
                y: rightScale(d.lineValue),
                size: 4,
                fill: lineColor,
                stroke: theme.axisColor || '#000',
              })}
            </g>
          ))}

          {/* Invisible hit targets on line points (always active) */}
          {data.map((d, i) => (
            <circle
              key={`hit-${i}`}
              cx={(xScale(d.name) || 0) + bandwidth / 2}
              cy={rightScale(d.lineValue)}
              r={8}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseMove={(e) => showTip(e, `${formatCompactNumber(d.barValue, 1)} · ${formatCompactNumber(d.lineValue, 1)}`)}
            />
          ))}

          {/* Left axis (bar scale) */}
          <AxisLeft
            scale={leftScale}
            stroke={theme.axisColor || '#000'}
            strokeWidth={theme.spineWidth ?? 1}
            tickStroke={theme.axisColor || '#000'}
            tickLength={yTickLength}
            tickFormat={getCompactTickFormatter(leftScale.domain()[1])}
            tickLabelProps={() => ({
              ...tickLabelProps(),
              dx: -yTickGap,
              dy: yTickBaselineNudge,
              textAnchor: 'end',
            })}
          />

          {/* Right axis (line scale) */}
          <AxisRight
            scale={rightScale}
            left={innerWidth}
            stroke={theme.axisColor || '#000'}
            strokeWidth={theme.spineWidth ?? 1}
            tickStroke={theme.axisColor || '#000'}
            tickLength={yTickLength}
            tickFormat={getCompactTickFormatter(rightScale.domain()[1])}
            tickLabelProps={() => ({
              ...tickLabelProps(),
              dx: rightTickDx,
              dy: yTickBaselineNudge,
              textAnchor: 'start',
            })}
          />

          {/* Bottom axis (categories, adaptive labels) */}
          <g>
            <line
              x1={0}
              y1={innerHeight}
              x2={innerWidth}
              y2={innerHeight}
              stroke={theme.axisColor || '#000'}
              strokeWidth={theme.spineWidth ?? 1}
            />
            {xAxis.tickValues.map((tickVal, i) => {
              const cx = (xScale(tickVal) || 0) + bandwidth / 2;
              const cy = innerHeight + xTickGap;
              return (
                <text
                  key={`xtick-${i}`}
                  x={cx}
                  y={cy}
                  dominantBaseline={xAxis.tickLabelProps.dominantBaseline || (xAxis.rotation ? 'auto' : 'hanging')}
                  textAnchor={xAxis.tickLabelProps.textAnchor || 'middle'}
                  fontSize={xAxis.fontSize}
                  fill={theme.tickColor || theme.foreground || '#000'}
                  fontFamily={theme.fontBody || 'Cohere Text'}
                  transform={xAxis.rotation ? `rotate(-${xAxis.rotation}, ${cx}, ${cy})` : undefined}
                >
                  {xAxis.tickFormat(tickVal)}
                </text>
              );
            })}
          </g>

          {/* Axis labels */}
          {config.xlabel && (
            <text
              x={innerWidth / 2}
              y={innerHeight + xLanes.titleOffsetPx}
              textAnchor="middle"
              {...axisLabelProps}
            >
              {config.xlabel}
            </text>
          )}
          {config.leftLabel && (
            <text
              x={-innerHeight / 2}
              y={-leftLanes.titleOffsetPx}
              transform="rotate(-90)"
              textAnchor="middle"
              {...axisLabelProps}
            >
              {config.leftLabel}
            </text>
          )}
          {axisDebugEnabled && (
            <AxisLaneDebugOverlay
              lanes={[
                {
                  axisSide: 'left',
                  tickLabelOffsetPx: leftLanes.tickLabelOffsetPx,
                  titleOffsetPx: leftLanes.titleOffsetPx,
                  clamped: leftLanes.clamped,
                  spineX: 0,
                  spineY1: 0,
                  spineY2: innerHeight,
                },
                {
                  axisSide: 'right',
                  tickLabelOffsetPx: rightLanes.tickLabelOffsetPx,
                  titleOffsetPx: rightLanes.titleOffsetPx,
                  clamped: rightLanes.clamped,
                  spineX: innerWidth,
                  spineY1: 0,
                  spineY2: innerHeight,
                },
                {
                  axisSide: 'bottom',
                  tickLabelOffsetPx: xLanes.tickLabelOffsetPx,
                  titleOffsetPx: xLanes.titleOffsetPx,
                  clamped: xLanes.clamped,
                  spineY: innerHeight,
                  spineX1: 0,
                  spineX2: innerWidth,
                },
              ]}
            />
          )}
        </Group>
        {config.rightLabel && (
          <text
            x={rightLabelX}
            y={rightLabelY}
            transform={`rotate(90, ${rightLabelX}, ${rightLabelY})`}
            textAnchor="middle"
            {...axisLabelProps}
          >
            {config.rightLabel}
          </text>
        )}
      </svg>
      <Tip />
    </div>
  );
}

export default ComboPreview;
